import PropTypes from 'prop-types';
import React, { Component } from 'react';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Checkbox } from 'synfrastructure';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import Loading from '../../common/components/Loading';
import numeral from 'numeral';
import InputScaffold from '../../common/forms/InputScaffold';
import ToggleScaffold from '../../common/forms/ToggleScaffold';
import TwoStepButton from '../../common/components/TwoStepButton';
import Icon from '../../common/icons/Icon';
import { createQuiz, deleteQuiz, getQuiz, editingQuiz, updateQuiz } from '../actions';

const DEFAULT_QUESTION = {
  text: '',
  answers: [''],
  video_hint: null,
  correct_answer: 0,
};

const DEFAULT_QUIZLESS_STATE = {
  currentQuestionIndex: 0,
  editing: false,
  saving: false,
  quiz: null,
  errors: {},
};

// Immutably update a property on a quiz then return a new state object
const setOnQuizState = (key, quiz, value) => ({
  quiz: {
    ...quiz,
    [key]: value,
  },
});

// Immutably update a question in an array by index then return a new array
const setOnQuestionsArray = (index, questions, key, value) => {
  const nextQuestions = [...questions];
  nextQuestions[index] = {
    ...nextQuestions[index],
    [key]: value,
  };
  return nextQuestions;
};

class QuizBuilder extends Component {
  static propTypes = {
    lessonId: PropTypes.number.isRequired,
    loaded: PropTypes.bool,
    quizId: PropTypes.number,
    quiz: PropTypes.object,
  };

  state = DEFAULT_QUIZLESS_STATE;

  componentDidMount() {
    if (this.props.quizId) {
      this.props.dispatch(getQuiz(this.props.quizId));
    }
  }

  componentWillReceiveProps(nextProps) {
    if (!this.props.loaded && nextProps.loaded) {
      const quiz = this.formatImmutableQuiz(nextProps.quiz);

      this.setState({ quiz });
    }
  }

  componentWillUpdate(nextProps, nextState) {
    if (nextProps.quizId && nextProps.quizId !== this.props.quizId) {
      this.props.dispatch(getQuiz(nextProps.quizId));
    }

    if (this.state.editing !== nextState.editing) {
      this.props.dispatch(editingQuiz(nextState.editing));
    }
  }

  componentWillUnmount() {
    this.props.dispatch(editingQuiz(false));
  }

  formatImmutableQuiz(quiz) {
    const formattedQuiz = quiz.toJS();
    delete formattedQuiz.id;
    formattedQuiz.questions = formattedQuiz.questions.map(question => {
      if (question.video_hint) {
        question.video_hint = numeral(question.video_hint).format('00:00:00');
      }
      return question;
    });
    return formattedQuiz;
  }

  handleAddQuiz = () => {
    this.setState({
      editing: true,
      quiz: {
        min_score: 0,
        questions: [{ ...DEFAULT_QUESTION }],
      },
    });
  };

  handleAddQuestion = () => {
    this.setState(state => ({
      currentQuestionIndex: state.quiz.questions.length,
      editing: true,
      quiz: {
        ...state.quiz,
        questions: [...state.quiz.questions, { ...DEFAULT_QUESTION }],
      },
    }));
  };

  handleCancel = e => {
    e.preventDefault();
    if (this.props.quiz) {
      const quiz = this.formatImmutableQuiz(this.props.quiz);
      this.setState({
        currentQuestionIndex: 0,
        editing: false,
        errors: {},
        quiz,
      });
    } else {
      this.setState(DEFAULT_QUIZLESS_STATE);
    }
  };

  handleRemoveQuestion = () => {
    if (this.state.quiz.questions.length > 1) {
      this.setState(state => {
        const questions = state.quiz.questions;

        // Immutably remove questions[currentQuestionIndex]
        const nextQuestions = questions
          .slice(0, state.currentQuestionIndex)
          .concat(questions.slice(state.currentQuestionIndex + 1));

        return {
          currentQuestionIndex: Math.max(0, state.currentQuestionIndex - 1),
          editing: true,
          ...setOnQuizState('questions', state.quiz, nextQuestions),
        };
      });
    }
  };

  handleUpdateMinScore = e => {
    const minScore = Math.min(100, parseInt(e.target.value, 10) || 0);
    this.setState(state => setOnQuizState('min_score', state.quiz, minScore));
  };

  handleGoToQuestion = e => {
    const currentQuestionIndex = parseInt(e.target.value, 10);
    this.setState({ currentQuestionIndex });
  };

  handleUpdateQuestionText = e => {
    const text = e.target.value;
    this.setState(state => {
      const nextQuestions = setOnQuestionsArray(state.currentQuestionIndex, state.quiz.questions, 'text', text);
      return setOnQuizState('questions', state.quiz, nextQuestions);
    });
  };

  handleUpdateQuestionVideoHint = e => {
    const videoHint = e.target.value || null;

    this.setState(state => {
      const questions = setOnQuestionsArray(state.currentQuestionIndex, state.quiz.questions, 'video_hint', videoHint);
      return setOnQuizState('questions', state.quiz, questions);
    });
  };

  handleUpdateAnswer = (targetIndex, e) => {
    const answer = e.target.value;
    this.setState(state => {
      const answers = [...state.quiz.questions[state.currentQuestionIndex].answers];
      answers[targetIndex] = answer;

      const questions = setOnQuestionsArray(state.currentQuestionIndex, state.quiz.questions, 'answers', answers);
      return setOnQuizState('questions', state.quiz, questions);
    });
  };

  handleUpdateCorrectAnswer = e => {
    const answer = parseInt(e.target.value, 10);
    this.setState(state => {
      const questions = setOnQuestionsArray(state.currentQuestionIndex, state.quiz.questions, 'correct_answer', answer);
      return setOnQuizState('questions', state.quiz, questions);
    });
  };

  handleRemoveAnswer = (targetIndex, e) => {
    if (this.state.quiz.questions[this.state.currentQuestionIndex].answers.length > 1) {
      this.setState(state => {
        const currentAnswers = state.quiz.questions[state.currentQuestionIndex].answers;
        // Immutably remove targetIndex from currentAnswers
        const answers = currentAnswers.slice(0, targetIndex).concat(currentAnswers.slice(targetIndex + 1));

        let questions = setOnQuestionsArray(state.currentQuestionIndex, state.quiz.questions, 'answers', answers);

        // If this is the correct answer, set that to the previous answer
        if (state.quiz.questions[state.currentQuestionIndex].correct_answer === targetIndex) {
          const correctAnswer = Math.max(0, targetIndex - 1);
          questions = setOnQuestionsArray(state.currentQuestionIndex, questions, 'correct_answer', correctAnswer);
        }

        return setOnQuizState('questions', state.quiz, questions);
      });
    }
  };

  handleAddAnswer = e => {
    this.setState(state => {
      const answers = [...state.quiz.questions[state.currentQuestionIndex].answers];
      answers.push('');

      let questions = setOnQuestionsArray(state.currentQuestionIndex, state.quiz.questions, 'answers', answers);
      return setOnQuizState('questions', state.quiz, questions);
    });
  };

  handleDelete = e => {
    this.setState({ saving: true });

    if (this.props.quizId) {
      this.props.dispatch(deleteQuiz(this.props.quizId, this.props.lessonId));
    }

    this.setState(DEFAULT_QUIZLESS_STATE);
  };

  validate = quiz => {
    // Allows for HH:MM:SS, H:MM:SS, MM:SS, M:SS, SS, S, up to 23:59:59
    const hmsRegex = /^(((([0-9]|0[0-9]|1[0-9]|2[0-3]):[0-5])|([0-5]?))[0-9]:)?[0-5]?[0-9]$/;

    const errors = {};
    let firstErrorIndex = null;

    quiz.questions.forEach((question, i) => {
      let questionErrors = {};
      let answerErrors = {};

      if (!question.text.trim()) {
        questionErrors.text = 'Question cannot be blank';
      }

      if (question.video_hint && !hmsRegex.test(question.video_hint)) {
        questionErrors.video_hint = 'Invalid time format';
      }

      question.answers.forEach((answer, j) => {
        if (!answer.trim()) {
          answerErrors[j] = 'Answer cannot be blank';
        }
      });

      if (Object.keys(answerErrors).length) {
        questionErrors.answers = answerErrors;
      }

      if (Object.keys(questionErrors).length) {
        errors[i] = questionErrors;
        if (!firstErrorIndex) {
          firstErrorIndex = i;
        }
      }
    });

    return [errors, firstErrorIndex];
  };

  convertVideoHintTimesToSeconds(quiz) {
    const convertedQuiz = cloneDeep(quiz);
    convertedQuiz.questions = convertedQuiz.questions.map(question => {
      if (!question.video_hint) {
        return question;
      }

      const times = question.video_hint.split(':');
      let time = 0;
      for (let i = 0; times.length; i++) {
        switch (i) {
          case 0:
            time += parseInt(times.pop(), 10);
            break;
          case 1:
            time += 60 * parseInt(times.pop(), 10);
            break;
          case 2:
            time += 60 * 60 * parseInt(times.pop(), 10);
            break;
          default:
            break;
        }
      }

      question.video_hint = time;
      return question;
    });

    return convertedQuiz;
  }

  handleSubmit = e => {
    e.preventDefault();

    if (!this.state.editing) {
      this.setState({ editing: true });
    } else {
      const [errors, firstErrorIndex] = this.validate(this.state.quiz);

      if (!Object.keys(errors).length) {
        let submitAction = createQuiz.bind(null, this.props.lessonId);

        if (this.props.quizId) {
          submitAction = updateQuiz.bind(null, this.props.quizId);
        }

        this.setState({
          editing: false,
          saving: true,
        });

        const submitQuiz = this.convertVideoHintTimesToSeconds(this.state.quiz);

        this.props.dispatch(submitAction(submitQuiz)).then(() => {
          this.setState({
            saving: false,
            errors: {},
          });
        });
      } else {
        this.setState({
          currentQuestionIndex: firstErrorIndex,
          errors,
        });
      }
    }
  };

  renderNavButtons = (length, currentIndex, onGoToQuestion, onAddQuestion) => {
    const pageButtons = [];
    for (let i = 0; i < length; i++) {
      const questionHasErrors = this.state.errors[i];

      const classes = classNames({
        'quiz__stepper-button': true,
        'quiz__stepper-button--active': currentIndex === i && !questionHasErrors,
        'quiz__stepper-button--error': currentIndex !== i && questionHasErrors,
        'quiz__stepper-button--active-error': currentIndex === i && questionHasErrors,
      });
      pageButtons.push(
        <button type="button" className={classes} key={i} value={i} onClick={onGoToQuestion}>
          {i + 1}
        </button>
      );
    }
    return (
      <div className="quiz-builder__nav">
        <div>{pageButtons}</div>
        {this.state.editing ? (
          <button type="button" className="admin__add-button" onClick={onAddQuestion}>
            <Icon icon="Plus" />Add Question
          </button>
        ) : null}
      </div>
    );
  };

  renderQuestion = (question, editing, questionsLength) => {
    const { currentQuestionIndex, errors } = this.state;

    if (!editing) {
      return (
        <div className="quiz-builder__questions">
          <div className="quiz-builder__saved">
            <p>Question {currentQuestionIndex + 1}</p>
            <span>{question.text}</span>
          </div>
          <div className="quiz-builder__saved">
            <p className="admin-paper__edit-content__title">Answers</p>
            <ul>
              {question.answers.map((answer, i) => (
                <li key={i}>
                  {answer}
                  {i === question.correct_answer ? <em>(Correct)</em> : null}
                </li>
              ))}
            </ul>
          </div>
          {question.video_hint ? (
            <div className="quiz-builder__saved">
              <p className="admin-paper__edit-content__title">Video Hint</p>
              <div className="quiz-builder__saved__time">{question.video_hint}</div>
            </div>
          ) : null}
        </div>
      );
    }

    return (
      <div className="quiz-builder__questions">
        <button
          type="button"
          className="button--icon quiz-builder__delete-question"
          onClick={this.handleRemoveQuestion}
          disabled={questionsLength <= 1}
        >
          <Icon icon="Trash" />
        </button>
        <InputScaffold
          label={`Question ${currentQuestionIndex + 1}`}
          className="quiz-builder__question"
          validation={get(errors, `[${currentQuestionIndex}].text`)}
        >
          <input type="text" value={question.text} onChange={this.handleUpdateQuestionText} />
        </InputScaffold>
        <div className="quiz-builder__answers">
          <div className="quiz-builder__nav">
            <p className="admin-paper__edit-content__title">Answers</p>
            <button type="button" className="admin__add-button" onClick={this.handleAddAnswer}>
              <Icon icon="Plus" />Add Answer
            </button>
          </div>
          <ul>
            {question.answers.map((answer, i) => (
              <li key={i} className="quiz-builder__answer">
                <InputScaffold validation={get(errors, `[${currentQuestionIndex}].answers.[${i}]`)}>
                  <input type="text" value={answer} onChange={this.handleUpdateAnswer.bind(this, i)} />
                </InputScaffold>
                <ToggleScaffold label="Correct">
                  <Checkbox
                    id={`answer-checkbox-${i}`}
                    value={i}
                    checked={question.correct_answer === i}
                    onChange={this.handleUpdateCorrectAnswer}
                  />
                </ToggleScaffold>
                <button
                  type="button"
                  className="button--icon"
                  onClick={this.handleRemoveAnswer.bind(this, i)}
                  disabled={question.answers.length <= 1}
                >
                  <Icon icon="Trash" />
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div className="quiz-builder__other-inputs">
          <div>
            <p className="admin-paper__edit-content__title">Video Hint?</p>
            <InputScaffold label="Start Time" validation={get(errors, `[${currentQuestionIndex}].video_hint`)}>
              <input type="text" value={question.video_hint || ''} onChange={this.handleUpdateQuestionVideoHint} />
            </InputScaffold>
          </div>
        </div>
      </div>
    );
  };

  renderMinScore = (minScore, onChange, editing) => {
    if (editing) {
      return (
        <InputScaffold label="Min Score">
          <input type="number" onChange={onChange} value={minScore} />
        </InputScaffold>
      );
    }

    return (
      <div className="quiz-builder__saved">
        <p className="admin-paper__edit-content__title">Min Score</p>
        <div className="quiz-builder__saved__time">{minScore}%</div>
      </div>
    );
  };

  renderSubmitButtons = editing => {
    if (!editing) {
      return (
        <div className="quiz-builder__buttons">
          <button className="button">Edit</button>
        </div>
      );
    }

    return (
      <div className="quiz-builder__buttons">
        <button type="button" className="button button--theme--plain" onClick={this.handleCancel}>
          Cancel
        </button>
        <button className="button">Save</button>
      </div>
    );
  };

  render() {
    if (this.state.saving || (this.props.quizId && !this.props.loaded)) {
      return <Loading />;
    }

    if (!this.state.quiz) {
      return (
        <div className="quiz-builder__add-quiz">
          This lesson does not have a quiz.
          <button type="button" className="admin__add-button" onClick={this.handleAddQuiz}>
            <Icon icon="Plus" />
            Add Quiz
          </button>
        </div>
      );
    }

    const { quiz: { questions, min_score }, currentQuestionIndex, editing } = this.state;

    return (
      <div>
        <div className="quiz-builder__header">
          <div>{this.renderMinScore(min_score, this.handleUpdateMinScore, editing)}</div>
          {editing ? <TwoStepButton buttonText="Delete Quiz" onConfirm={this.handleDelete} /> : null}
        </div>
        <form onSubmit={this.handleSubmit} className="quiz-builder">
          {this.renderNavButtons(
            questions.length,
            currentQuestionIndex,
            this.handleGoToQuestion,
            this.handleAddQuestion
          )}
          {this.renderQuestion(questions[currentQuestionIndex], editing, questions.length)}
          {this.renderSubmitButtons(editing)}
        </form>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  quiz: state.courses.quiz.get('data'),
  loaded: state.courses.quiz.get('loaded'),
});

export default withRouter(connect(mapStateToProps)(QuizBuilder));



// WEBPACK FOOTER //
// ./src/courses/quizzes/QuizBuilder.js